import { useState, type FormEvent } from 'react';

import type { AccountUser } from '../../components/screens/RunScreens';
import type { AppPlan } from './appPlans';
import { IconCard, IconGithub, IconLogOut, IconMail } from './appIcons';
import type { AppGitHubState } from './useAppAccount';

interface AppSettingsViewProps {
  accountUser: AccountUser | null;
  githubLogin: string;
  githubState: AppGitHubState;
  installationCount: number;
  canManageGitHub: boolean;
  plan: AppPlan;
  scansLeft: number;
  onConnectGitHub: () => void;
  onManageGitHub: () => void;
  onOpenPlans: () => void;
  /** Saves the account email; throws with a user-facing message on failure. */
  onSaveEmail: (email: string) => Promise<void>;
  /** Resolves to whether the session was ended. */
  onLogout: () => Promise<boolean>;
}

const GITHUB_STATUS: Record<AppGitHubState, string> = {
  loading: 'Checking connection',
  disconnected: 'Not connected',
  'needs-install': 'Connected, no repositories shared yet',
  connected: 'Connected'
};

function AppSettingsView(props: AppSettingsViewProps) {
  const { accountUser, githubLogin, githubState, installationCount, plan, scansLeft } = props;
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const login = accountUser?.githubLogin || githubLogin;
  const githubStatus = githubState === 'connected' && installationCount > 0
    ? `${GITHUB_STATUS.connected} · App installed on ${String(installationCount)} account${installationCount === 1 ? '' : 's'}`
    : GITHUB_STATUS[githubState];

  const logout = async () => {
    setIsLoggingOut(true);
    setLogoutError('');

    // On success the page leaves this view, so there is no state left to reset.
    if (!await props.onLogout()) {
      setIsLoggingOut(false);
      setLogoutError('Unable to log out. Please try again.');
    }
  };

  const editEmail = () => {
    setEmailDraft(accountUser?.email ?? '');
    setEmailError('');
  };

  const cancelEmail = () => {
    setEmailDraft(null);
    setEmailError('');
  };

  const saveEmail = (event: FormEvent) => {
    event.preventDefault();

    if (emailDraft === null) {
      return;
    }

    setIsSavingEmail(true);
    setEmailError('');
    props.onSaveEmail(emailDraft)
      .then(() => setEmailDraft(null))
      .catch((error: unknown) => {
        setEmailError(error instanceof Error && error.message ? error.message : 'The email could not be saved.');
      })
      .finally(() => setIsSavingEmail(false));
  };

  return (
    <div className="app-scroll">
      <div className="app-settings">
        <section className="app-settings-row" aria-labelledby="app-settings-account">
          <div className="app-settings-copy">
            <strong id="app-settings-account">Account</strong>
            <span>{login ? `Logged in with GitHub as @${login}` : 'Logged in with GitHub'}</span>
            {logoutError && <span className="app-widget-error" role="alert">{logoutError}</span>}
          </div>
          <button
            type="button"
            className="app-action-button danger"
            disabled={isLoggingOut}
            onClick={() => void logout()}
          >
            <IconLogOut aria-hidden={true} />
            {isLoggingOut ? 'Logging out...' : 'Log out'}
          </button>
        </section>

        <section className="app-settings-row" aria-labelledby="app-settings-email">
          {emailDraft === null ? (
            <>
              <div className="app-settings-copy">
                <strong id="app-settings-email">Email</strong>
                <span>{accountUser?.email || 'Not set'}</span>
                <span>Assessment results for uploaded ZIPs are sent here.</span>
              </div>
              <button type="button" className="app-action-button" disabled={!accountUser} onClick={editEmail}>
                <IconMail aria-hidden={true} />
                {accountUser?.email ? 'Change' : 'Add email'}
              </button>
            </>
          ) : (
            <form className="app-settings-form" onSubmit={saveEmail}>
              <label className="app-settings-copy" htmlFor="app-settings-email-input">
                <strong id="app-settings-email">Email</strong>
                <span>Assessment results for uploaded ZIPs are sent here.</span>
              </label>
              <div className="app-settings-form-controls">
                <input
                  id="app-settings-email-input"
                  className="app-filter-search"
                  type="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  value={emailDraft}
                  disabled={isSavingEmail}
                  autoFocus={true}
                  onChange={(event) => setEmailDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      cancelEmail();
                    }
                  }}
                />
                <button type="submit" className="app-action-button" disabled={isSavingEmail || !emailDraft.trim()}>
                  {isSavingEmail ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="app-action-button" disabled={isSavingEmail} onClick={cancelEmail}>
                  Cancel
                </button>
              </div>
              {emailError && <span className="app-widget-error" role="alert">{emailError}</span>}
            </form>
          )}
        </section>

        <section className="app-settings-row" aria-labelledby="app-settings-github">
          <div className="app-settings-copy">
            <strong id="app-settings-github">GitHub App</strong>
            <span>{githubStatus}</span>
          </div>
          {githubState === 'disconnected' ? (
            <button type="button" className="app-action-button" onClick={props.onConnectGitHub}>
              <IconGithub aria-hidden={true} />
              Connect GitHub
            </button>
          ) : (
            <button
              type="button"
              className="app-action-button"
              disabled={githubState === 'loading' || !props.canManageGitHub}
              onClick={props.onManageGitHub}
            >
              <IconGithub aria-hidden={true} />
              {githubState === 'needs-install' ? 'Install app' : 'Manage access'}
            </button>
          )}
        </section>

        <section className="app-settings-row" aria-labelledby="app-settings-plan">
          <div className="app-settings-copy">
            <strong id="app-settings-plan">Plan</strong>
            <span>
              {plan.id === 'none'
                ? `${plan.name} · ${plan.messagesPerDay} AppSec questions per day`
                : `${plan.name} · ${scansLeft} assessment${scansLeft === 1 ? '' : 's'} left this ${plan.period}`}
            </span>
          </div>
          <button type="button" className="app-action-button" onClick={props.onOpenPlans}>
            <IconCard aria-hidden={true} />
            View plans
          </button>
        </section>
      </div>
    </div>
  );
}

export default AppSettingsView;
